<script setup lang="ts">
import { userModule } from '@/store/user/module'
import { computed, onMounted, useTemplateRef } from 'vue'
import UIKit from '@pocketprep/ui-kit'

const {
    ModalContainer,
    Button: PocketButton,
} = UIKit

const { numOfAnsweredQuestions, numOfAssignmentQuestions } = defineProps<{
    numOfAnsweredQuestions: number
    numOfAssignmentQuestions: number
}>()

const emit = defineEmits<{
    'close': []
    'submit': []
    'cancel': []
}>()

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const titleEl = useTemplateRef('submit-assignment-modal__title')

onMounted(() => {
    setTimeout(() => {
        titleEl.value?.focus()
    }, 500)
})

const emitSubmit = () => {
    emit('submit')
}

const emitCancel = () => {
    emit('cancel')
}
</script>

<template>
    <Portal to="modal">
        <ModalContainer
            key="submitAssignmentModal"
            :show-close-button="true"
            :is-dark-mode="isDarkMode"
            @close="emit('close')"
        >
            <div class="submit-assignment-modal">
                <h1
                    ref="submit-assignment-modal__title"
                    class="submit-assignment-modal__title"
                    tabindex="-1"
                >
                    Submit Assignment?
                </h1>
                <div
                    v-if="numOfAnsweredQuestions < numOfAssignmentQuestions"
                    v-dark
                    class="submit-assignment-modal__text"
                >
                    You've answered {{ numOfAnsweredQuestions }} of
                    {{ numOfAssignmentQuestions }} question{{ numOfAssignmentQuestions > 1 ? 's' : '' }}.
                    If you submit, unanswered questions will be marked incorrect.
                </div>
                <div
                    v-else
                    v-dark
                    class="submit-assignment-modal__text"
                >
                    You've answered {{ numOfAnsweredQuestions }} of
                    {{ numOfAssignmentQuestions }} question{{ numOfAssignmentQuestions > 1 ? 's' : '' }}.
                    Submit to score your assignment.
                </div>
                <div class="submit-assignment-modal__actions">
                    <PocketButton
                        type="secondary"
                        class="submit-assignment-modal__cancel-btn"
                        :is-dark-mode="isDarkMode"
                        @click="emitCancel"
                    >
                        Cancel
                    </PocketButton>
                    <PocketButton
                        class="submit-assignment-modal__submit-btn"
                        :is-dark-mode="isDarkMode"
                        @click="emitSubmit"
                    >
                        Submit Assignment
                    </PocketButton>
                </div>
            </div>
        </ModalContainer>
    </Portal>
</template>

<style lang="scss" scoped>
.submit-assignment-modal {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 35px 41px 26px 38px;
    width: 384px;
    margin: 0 auto;

    &__title {
        font-weight: 600;
        font-size: 18px;
        line-height: 22px;
        margin: 0 0 12px 0;
        outline: none;
    }

    &__text {
        color: $ash;
        font-size: 15px;
        line-height: 22px;
        text-align: center;
        margin-bottom: 29px;

        &--dark {
            color: $fog;
        }
    }

    &__cancel-btn {
        margin-right: 4px;
    }
}
</style>
